import React, { useEffect, useMemo, useState } from 'react';
import useRealtimeRefresh from '../../realtime/useRealtimeRefresh';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Collapse,
  Dialog, DialogContent, DialogTitle,
  Divider, IconButton, InputLabel, LinearProgress, MenuItem,
  Select, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import InsightsIcon from '@mui/icons-material/Insights';

import {
  fetchRequest as fetchTournament,
  clearState as clearTournament,
} from '../../redux/actions/discovery/tournament';
import {
  fetchRequest as startTournament,
  clearState as clearStart,
} from '../../redux/actions/discovery/startTournament';
import {
  fetchRequest as tournamentControl,
  clearState as clearControl,
} from '../../redux/actions/discovery/tournamentControl';
import {
  fetchRequest as selectChampion,
  clearState as clearSelectChampion,
} from '../../redux/actions/discovery/selectChampion';
import { promoteRequest, clearState as clearPromote } from '../../redux/actions/promotion/promote';
import { CMButton, CMButtonOutlined, CMButtonOutlinedSmall, CMFormControlDark } from '../../containers/styledComponents';
import { modelDisplayName, modelTechnicalName, humanizeTraits } from './modelNames';
import AccuracyInfoTooltip from '../common/AccuracyInfoTooltip';

const ENGINE_META = {
  xgboost: { label: 'XGBoost', color: 'primary' },
  sklearn: { label: 'sklearn', color: 'secondary' },
  foundation: { label: 'Foundation', color: 'warning' },
  blueprint: { label: 'Your model', color: 'info' },
};

// Foundation entries carry the actual backbone family — show that
// instead of the generic engine label (e.g. "Chronos-2", "PredictFM").
const FAMILY_LABELS = {
  chronos_2: 'Chronos-2',
  chronos_bolt: 'Chronos-Bolt',
  timesfm: 'TimesFM',
  moirai: 'Moirai',
  lag_llama: 'Lag-Llama',
  ttm: 'TTM',
  moment: 'MOMENT',
  predictfm: 'PredictFM',
};

const ENTRY_STATUS_META = {
  pending: { label: 'Pending', color: 'default' },
  running: { label: 'Running…', color: 'info' },
  // Foundation Flow B: the LoRA adapter is training on the GPU pool while
  // the fitting slot is released — still live work, just a different stage.
  adapting: { label: 'Training adapter…', color: 'info' },
  completed: { label: 'Done', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  unavailable: { label: 'Unavailable', color: 'default' },
  cancelled: { label: 'Cancelled', color: 'default' },
};

// Entry statuses that mean "actively working right now".
const ACTIVE_ENTRY_STATUSES = new Set(['running', 'adapting']);

// An entry is cancellable while it's still pending or actively working.
const CANCELLABLE_ENTRY_STATUSES = new Set(['pending', 'running', 'adapting']);

// The winning model trains a second time (full-history serving training)
// after the leaderboard; this typically takes ~10-12 min. We show elapsed +
// progress so the wait reads as work, and only hint "running long" well past
// the norm (the backend's own stall/fallback guard is longer still).
const EXPECTED_TRAIN_MIN = 12;
const SLOW_TRAIN_MIN = 20;

const fmtElapsed = (min) => {
  if (min == null) return '';
  if (min < 1) return 'just started';
  return `${Math.floor(min)} min`;
};

const fmtScore = (entry, metric) => {
  if (entry?.score == null) return '—';
  if (metric?.name === 'accuracy' || metric?.name === 'avg_precision') {
    return `${(entry.score * 100).toFixed(1)}%`;
  }
  return Number(entry.score).toPrecision(4);
};

// Metrics that are ALREADY a 0-1 quality fraction (classification) — shown as
// a percentage directly. Everything else (rmse/crps/mae) is an error on the
// data's own scale with no intuitive ceiling, so it has no native %.
const PERCENT_METRICS = new Set([
  'accuracy', 'avg_precision', 'auc', 'roc_auc', 'precision', 'recall', 'f1', 'hit_rate',
]);

// A single, understandable 0-100% reading of model quality regardless of the
// underlying metric. Classification goals score as accuracy/AUC already;
// regression & range goals (rmse/crps) have no intuitive scale, so we surface
// the backend's cross-validated WMAPE accuracy (100% − average error / actual).
// Returns null when no % reading is available (caller falls back to the raw
// metric). Note: rows are still RANKED by the goal's native metric.
const scorePct = (entry, metric) => {
  if (entry?.score == null) return null;
  if (PERCENT_METRICS.has(String(metric?.name || '').toLowerCase())) {
    return entry.score * 100;
  }
  if (entry.accuracy_score != null) return entry.accuracy_score;
  return null;
};

// One decimal so near-tied boards still read differently (99.4% vs 98.9% —
// whole-percent rounding collapsed them all to "99%"). Trailing .0 dropped.
const fmtPct1 = (v) => `${Number(Number(v).toFixed(1))}%`;

// Metrics where a HIGHER number is better (classification / skill). Everything
// else (rmse, crps, mae, …) is an error — lower is better. Drives the "goodness"
// direction so a taller bar / fuller meter always means a better model.
const HIGHER_BETTER = new Set([
  'accuracy', 'avg_precision', 'precision', 'recall', 'f1', 'auc', 'roc_auc',
  'r2', 'skill', 'skill_score', 'hit_rate',
]);
const lowerIsBetter = (metric) => !HIGHER_BETTER.has(String(metric?.name || '').toLowerCase());

// Map a raw score to 0..1 goodness within a shared [lo, hi] range so bars and
// meters are comparable ACROSS entries (best → 1, worst → 0).
const goodnessOf = (v, range, lower) => {
  if (!range || range.hi === range.lo) return 1;
  const g = lower ? (range.hi - v) / (range.hi - range.lo) : (v - range.lo) / (range.hi - range.lo);
  return Math.max(0, Math.min(1, g));
};

const foldStats = (scores) => {
  const xs = (scores || []).map(Number).filter((v) => Number.isFinite(v));
  if (!xs.length) return null;
  const mean = xs.reduce((a, b) => a + b, 0) / xs.length;
  const variance = xs.reduce((a, b) => a + (b - mean) ** 2, 0) / xs.length;
  const std = Math.sqrt(variance);
  return { mean, std, cv: mean !== 0 ? Math.abs(std / mean) : 0, n: xs.length };
};

// Spread across folds, as a one-word reliability read. A model with a great
// mean but wildly varying folds is risky; this surfaces that at a glance.
const consistencyLabel = (cv) => {
  if (cv == null) return null;
  if (cv < 0.1) return { text: 'tight', color: 'success.main' };
  if (cv < 0.25) return { text: 'steady', color: 'text.secondary' };
  return { text: 'variable', color: 'warning.main' };
};

/**
 * Per-fold scores as a tiny shared-scale bar chart: one bar per fold, height =
 * goodness on the board-wide range (taller = better, for any metric), with a
 * spread read ("±cv% · steady") that conveys how consistent the model is
 * across folds. Hover for the exact per-fold numbers.
 */
const FoldBars = ({ scores, range, lower, highlight, metricName }) => {
  const theme = useTheme();
  const xs = (scores || []).map(Number).filter((v) => Number.isFinite(v));
  if (!xs.length) return <Typography variant="caption" color="text.disabled">—</Typography>;
  const stats = foldStats(xs);
  const cons = consistencyLabel(stats?.cv);
  const H = 22; const bw = 5; const gap = 3;
  const W = xs.length * (bw + gap) - gap;
  const barColor = highlight ? theme.palette.success.main : theme.palette.primary.main;
  const fmtFold = (v) => (metricName === 'accuracy' || metricName === 'avg_precision'
    ? `${(v * 100).toFixed(1)}%` : Number(v).toPrecision(4));
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
      <Tooltip
        arrow
        title={(
          <Box sx={{ fontFamily: 'monospace', fontSize: 11 }}>
            {xs.map((v, i) => (
              <div key={i}>{`fold ${i + 1}: ${fmtFold(v)}`}</div>
            ))}
          </Box>
        )}
      >
        <svg width={Math.max(W, bw)} height={H} style={{ display: 'block' }}>
          {xs.map((v, i) => {
            const g = goodnessOf(v, range, lower);
            const h = Math.max(2, g * (H - 2));
            return (
              <rect
                key={i}
                x={i * (bw + gap)}
                y={H - h}
                width={bw}
                height={h}
                rx={1.2}
                fill={barColor}
                opacity={highlight ? 0.92 : 0.5}
              />
            );
          })}
        </svg>
      </Tooltip>
      {stats && cons && (
        <Tooltip arrow title="Spread across folds — how consistent the model is from fold to fold (lower is more reliable)">
          <Typography variant="caption" color={cons.color}>
            ±{(stats.cv * 100).toFixed(0)}% · {cons.text}
          </Typography>
        </Tooltip>
      )}
    </Stack>
  );
};

// Per-entry stage copy for the live fitting bar. Foundation Flow-B entries
// train a LoRA adapter ("adapting") before the fold-scoring pass; everything
// else is fold scoring from the start.
const ENTRY_STAGE_LABEL = {
  adapting: 'Training adapter',
  warming: 'Waking GPU…',
  scoring: 'Scoring folds',
};

/**
 * Live per-entry fitting progress, fed by the backend's
 * ``goal.tournament.progress`` events ({folds_done, n_folds, pct, stage}).
 * Determinate once a percentage arrives, indeterminate before the first tick
 * (or for legacy rows without progress) so a running entry never looks idle.
 */
const EntryFitProgress = ({ progress }) => {
  const pct = typeof progress?.pct === 'number'
    ? Math.min(99, Math.max(2, Math.round(progress.pct))) : null;
  const nFolds = progress?.n_folds;
  const foldsDone = progress?.folds_done;
  const stage = progress?.stage;
  const detail = stage === 'adapting'
    ? 'Training adapter…'
    : stage === 'warming'
      ? 'Waking GPU… first forecast can take a few minutes'
      : (nFolds
        ? `Scoring fold ${Math.min(foldsDone || 0, nFolds)}/${nFolds}`
        : (ENTRY_STAGE_LABEL[stage] || 'Starting…'));
  return (
    <Box sx={{ minWidth: 120 }}>
      <LinearProgress
        variant={pct != null ? 'determinate' : 'indeterminate'}
        value={pct ?? undefined}
        sx={{ height: 5, borderRadius: 3 }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
        {detail}{pct != null ? ` · ${pct}%` : ''}
      </Typography>
    </Box>
  );
};

const fmtDay = (d) => (Number.isNaN(d?.getTime?.())
  ? null
  : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }));

// "2.3 years" / "5 months" / "18 days" — a readable span between two dates.
const humanSpan = (ms) => {
  if (!(ms > 0)) return null;
  const days = ms / 86400000;
  if (days >= 730) return `${(days / 365).toFixed(1)} years`;
  if (days >= 60) return `${Math.round(days / 30)} months`;
  if (days >= 14) return `${Math.round(days / 7)} weeks`;
  return `${Math.max(1, Math.round(days))} days`;
};

// Map an average bar spacing (seconds) to a friendly cadence label.
const humanCadence = (sec) => {
  if (!(sec > 0)) return null;
  const map = [
    [60, 'per-minute'], [300, '5-minute'], [900, '15-minute'], [1800, '30-minute'],
    [3600, 'hourly'], [21600, '6-hour'], [86400, 'daily'], [604800, 'weekly'],
    [2592000, 'monthly'],
  ];
  let best = map[0];
  for (const m of map) { if (Math.abs(sec - m[0]) < Math.abs(sec - best[0])) best = m; }
  return best[1];
};

/**
 * Minimal context for the leaderboard: the size, time window and validation of
 * the data each model was fitted on — in plain language, not just raw numbers.
 */
const HowFittingWorks = ({ tournament }) => {
  const nFolds = tournament?.n_folds;
  const nRows = Number(tournament?.n_grid_rows) || 0;
  const start = tournament?.grid_start ? new Date(tournament.grid_start) : null;
  const end = tournament?.grid_end ? new Date(tournament.grid_end) : null;
  const startLbl = start && fmtDay(start);
  const endLbl = end && fmtDay(end);
  const span = (start && end) ? humanSpan(end.getTime() - start.getTime()) : null;
  const cadence = (start && end && nRows > 1)
    ? humanCadence((end.getTime() - start.getTime()) / 1000 / (nRows - 1)) : null;

  if (!nRows && !nFolds && !startLbl) return null;

  const Stat = ({ label, value, hint, tip }) => {
    const body = (
      <Stack spacing={0.1} sx={{ cursor: tip ? 'help' : 'default' }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10.5 }}
        >
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.25 }}>
          {value}
          {hint && (
            <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary', ml: 0.75 }}>
              {hint}
            </Box>
          )}
        </Typography>
      </Stack>
    );
    return tip ? <Tooltip arrow title={tip}>{body}</Tooltip> : body;
  };

  return (
    <Box
      sx={(t) => ({
        px: 1.75, py: 1.25, borderRadius: 2,
        border: `1px solid ${t.palette.divider}`,
        display: 'flex', alignItems: 'center', gap: 3.5, flexWrap: 'wrap', rowGap: 1.25,
      })}
    >
      {nRows > 0 && (
        <Stat
          label="Data points"
          value={nRows.toLocaleString()}
          hint={cadence ? `· ${cadence}` : null}
          tip="Historical observations (rows on the modeling grid) each model is fitted and cross-validated on."
        />
      )}
      {startLbl && endLbl && (
        <Stat
          label="Date range"
          value={`${startLbl} → ${endLbl}`}
          hint={span ? `· ${span}` : null}
          tip="The span of history the models learn from, oldest to most recent."
        />
      )}
      {nFolds > 0 && (
        <Stat
          label="Folds"
          value={`${nFolds} walk-forward splits`}
          tip="Each model trains on earlier data and is tested on the next unseen slice, repeated across these splits — so the score isn't a one-off lucky split."
        />
      )}
    </Box>
  );
};

// A single labelled metric tile in the entry-details modal.
const StatTile = ({ label, value, sub, color = 'text.primary', tip }) => {
  const tile = (
    <Box
      sx={(t) => ({
        p: 1.25, borderRadius: 1.5, border: `1px solid ${t.palette.divider}`,
        minWidth: 124, flex: '1 1 124px', cursor: tip ? 'help' : 'default',
      })}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color, lineHeight: 1.25, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
    </Box>
  );
  return tip ? <Tooltip arrow title={tip}>{tile}</Tooltip> : tile;
};

/**
 * Click-through detail for a single leaderboard entry: the headline quality
 * score, the goal's raw metric (RMSE/CRPS), skill, fold-by-fold breakdown,
 * consistency, and the model/protocol facts — everything useful that doesn't
 * fit in a table row.
 */
const EntryDetailDialog = ({ entry, metric, lower, foldRange, isWinner, onClose }) => {
  if (!entry) return null;
  const e = entry;
  const engineLabel = e.engine === 'foundation' && e.family
    ? (FAMILY_LABELS[e.family] || e.family)
    : (ENGINE_META[e.engine]?.label || e.engine);
  const pct = scorePct(e, metric);
  const mname = String(metric?.name || '').toLowerCase();
  const isPctMetric = PERCENT_METRICS.has(mname);
  // RMSE: the ranking metric for value goals, else the point error captured
  // alongside the distributional score. CRPS: the ranking metric for range
  // goals. Any other regression metric (e.g. mae) falls back to a generic tile.
  const rmseVal = mname === 'rmse' ? e.score : (e.point_rmse != null ? e.point_rmse : null);
  const crpsVal = mname === 'crps' ? e.score : null;
  const otherMetricVal = (!isPctMetric && mname !== 'rmse' && mname !== 'crps') ? e.score : null;
  const stats = foldStats(e.fold_scores);
  const cons = consistencyLabel(stats?.cv);
  const fmtFold = (v) => (metric?.name === 'accuracy' || metric?.name === 'avg_precision'
    ? `${(v * 100).toFixed(1)}%` : Number(v).toPrecision(4));
  const traits = humanizeTraits(e.affinity_matches);
  const techName = modelTechnicalName(e);
  const detailRows = [
    ['Rank', e.rank != null ? `#${e.rank}` : null],
    ['Status', (ENTRY_STATUS_META[e.status]?.label) || e.status],
    // The exact algorithm identity — the friendly strategy name is the
    // headline, this is where technical users find the real thing.
    ['Technical name', techName],
    ['Engine', engineLabel],
    ['Cross-validation', e.protocol === 'walk_forward' ? 'Walk-forward'
      : e.protocol === 'heldout_windows' ? 'Held-out windows' : e.protocol],
    ['Folds', (e.fold_scores || []).length || null],
    ['Range method', e.range_method
      ? (e.range_method === 'native_quantiles' ? 'Native quantiles' : 'Conformal wrap') : null],
    ['Best rounds', e.best_rounds],
    ['Sequence length', e.sequence_length],
    ['Lookback', e.lookback],
    ['Eval windows', e.n_eval_rows],
  ].filter(([, v]) => v != null && v !== '');
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>{modelDisplayName(e)}</Typography>
          <Chip size="small" variant="outlined" label={engineLabel} />
          {e.flow && (
            <Chip
              size="small"
              variant="outlined"
              color={e.flow === 'A' ? 'warning' : 'default'}
              label={e.flow === 'A' ? 'Baseline — no drivers' : 'Fine-tuned'}
            />
          )}
          {e.belief_weighted && (
            <Tooltip
              title="This model weights each feature by its discovery confidence (mu) — high-confidence drivers get full pull, low-confidence ones are shrunk toward zero. Impact never shrinks a feature; it only ranks them."
              arrow
            >
              <Chip size="small" color="secondary" variant="outlined" icon={<InsightsIcon />} label="Belief-weighted" />
            </Tooltip>
          )}
          {isWinner && <Chip size="small" color="success" icon={<EmojiEventsIcon />} label="Champion" />}
        </Stack>
        {techName && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {techName}
          </Typography>
        )}
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 12, right: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
            {pct != null && (
              <StatTile
                label="Accuracy"
                value={fmtPct1(pct)}
                color={isWinner ? 'success.main' : 'text.primary'}
                sub={isPctMetric ? 'detector AUC' : '100% − WAPE'}
                tip="How close the forecasts land to the actuals on aggregate (100% − WAPE), measured on held-out folds."
              />
            )}
            {pct != null && !isPctMetric && (
              <StatTile
                label="WAPE"
                value={`${Math.max(0, 100 - pct).toFixed(1)}%`}
                color="text.primary"
                sub="lower is better"
                tip="Weighted Absolute Percentage Error: total absolute error divided by total actuals on held-out folds. Accuracy is 100% − WAPE."
              />
            )}
            {e.skill_score != null && (
              <StatTile
                label="Skill vs persistence"
                value={`${Math.round(e.skill_score)}%`}
                sub="walk-forward folds"
                tip="Walk-forward skill: how much this tournament fit beats carrying the forecast-origin value forward over the goal's horizon, on held-out folds. Measured on the tournament's own fit — the Forecast tab shows the deployed model's back-test, which can differ. 0% = no better than persistence."
              />
            )}
            {crpsVal != null && (
              <StatTile
                label="CRPS"
                value={Number(crpsVal).toPrecision(4)}
                sub="ranking metric · lower is better"
                tip="Continuous Ranked Probability Score — grades the whole predicted RANGE / uncertainty against what actually happened, not just the point forecast (computed here as pinball loss across quantiles). Same units as the data; lower is better, and the board ranks by it."
              />
            )}
            {rmseVal != null && (
              <StatTile
                label="RMSE"
                value={Number(rmseVal).toPrecision(4)}
                sub={mname === 'rmse' ? 'ranking metric · lower is better' : 'median-forecast error'}
                tip={`Root Mean Squared Error — the typical size of the miss between the${crpsVal != null ? ' median' : ''} forecast and the actual, in the data's own units (big misses are penalised harder). Lower is better.${crpsVal != null ? ' This is the point-accuracy behind the distributional CRPS.' : ''}`}
              />
            )}
            {otherMetricVal != null && (
              <StatTile
                label={metric?.name || 'score'}
                value={Number(otherMetricVal).toPrecision(4)}
                sub="ranking metric · lower is better"
                tip="The goal's native ranking metric (averaged across folds). Lower is better."
              />
            )}
            {stats && cons && (
              <StatTile
                label="Consistency"
                value={`±${(stats.cv * 100).toFixed(0)}%`}
                sub={cons.text}
                color={cons.color}
                tip="Spread of the score from fold to fold — lower is more reliable."
              />
            )}
            {e.duration_s != null && (
              <StatTile label="Fit time" value={`${Math.round(e.duration_s)}s`} />
            )}
          </Stack>

          {(e.fold_scores || []).length > 0 && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}
              >
                Per-fold {metric?.name ? `· ${metric.name}` : ''}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <FoldBars
                  scores={e.fold_scores}
                  range={foldRange}
                  lower={lower}
                  highlight={isWinner}
                  metricName={metric?.name}
                />
              </Box>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {e.fold_scores.map((v, i) => (
                  <Chip
                    key={i}
                    size="small"
                    variant="outlined"
                    label={`F${i + 1}: ${fmtFold(Number(v))}`}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}
            >
              Details
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.75 }}>
              {detailRows.map(([k, v]) => (
                <Stack key={k} direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="body2" color="text.secondary">{k}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>{String(v)}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          {traits.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Picked for this data — your history shows</Typography>
              <Typography variant="body2" color="info.main">{traits.join(' · ')}</Typography>
            </Box>
          )}

          {e.error && <Alert severity="error">{String(e.error)}</Alert>}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

/**
 * The Tournament leaderboard (spec §4.5, §7 J4): deterministic preset
 * entries, per-fold scores, the winner highlighted, and the champion's
 * "promote" CTA through the standard promotion gate. The user sees a
 * leaderboard — never a config form.
 */
const TournamentBoard = ({ goalId }) => {
  const dispatch = useDispatch();
  const [showAll, setShowAll] = useState(true);
  const [detailId, setDetailId] = useState(null);
  // Which model the user has picked to deploy. ``null`` tracks the
  // auto-selected best; once the user explicitly chooses, we hold their pick.
  const [selectedPreset, setSelectedPreset] = useState(null);

  const tournament = useSelector((s) => s.discovery_tournament?.response?.tournament);
  const startState = useSelector((s) => s.discovery_start_tournament);
  const controlState = useSelector((s) => s.discovery_tournament_control) || {};
  const promoteState = useSelector((s) => s.model_promote) || {};
  const selectState = useSelector((s) => s.discovery_select_champion) || {};

  const status = tournament?.status;
  const active = status === 'queued' || status === 'running';

  // The entry table stays expanded by default (results stream in live and the
  // leaderboard is the point of the card). A new fit force-expands it; manual
  // toggles otherwise stick — we never auto-collapse it out from under the user.
  useEffect(() => { if (active) setShowAll(true); }, [active]);

  useEffect(() => {
    if (goalId) {
      dispatch(fetchTournament({ goalId }));
    }
    return () => {
      dispatch(clearTournament());
      dispatch(clearStart());
      dispatch(clearControl());
      dispatch(clearPromote());
      dispatch(clearSelectChampion());
    };
  }, [goalId, dispatch]);

  // Live (no polling): refetch the board whenever the backend reports a
  // tournament/entry transition, a run transition (the tournament is created
  // mid-run), a champion TRAINING transition, a DEPLOYMENT transition (the
  // board flips "Deploying…"→"Deployed"), or an autopilot PROMOTION verdict
  // (quality-gate block). All of these reach us on the goal + workspace
  // channels this page is subscribed to. Reconnect (msg=null) triggers a
  // reconcile refetch too.
  useRealtimeRefresh(
    (msg) => typeof msg.event === 'string' && (
      msg.event.indexOf('goal.tournament.') === 0
      || msg.event.indexOf('goal.run.') === 0
      || msg.event.indexOf('goal.status') === 0
      || msg.event.indexOf('training.') === 0
      || msg.event.indexOf('deployment.') === 0
      || msg.event.indexOf('goal.promotion.') === 0
    ),
    () => { if (goalId) dispatch(fetchTournament({ goalId })); },
  );

  const entries = useMemo(() => {
    const list = tournament?.entries || [];
    return [...list].sort((a, b) => {
      if (a.rank != null && b.rank != null) return a.rank - b.rank;
      if (a.rank != null) return -1;
      if (b.rank != null) return 1;
      return 0;
    });
  }, [tournament]);

  const metric = tournament?.metric;
  const lower = lowerIsBetter(metric);

  // Resolve the open detail modal from the LIVE entry list (by id) so it keeps
  // updating if the board refetches while it's open.
  const detailEntry = useMemo(
    () => entries.find((e) => e.preset_id === detailId) || null,
    [entries, detailId],
  );

  // Board-wide ranges so per-fold bars and the "vs best" meter are comparable
  // ACROSS entries (not just within one row).
  const foldRange = useMemo(() => {
    let lo = Infinity; let hi = -Infinity;
    (tournament?.entries || []).forEach((e) => (e.fold_scores || []).forEach((s) => {
      const v = Number(s);
      if (Number.isFinite(v)) { lo = Math.min(lo, v); hi = Math.max(hi, v); }
    }));
    return Number.isFinite(lo) && Number.isFinite(hi) ? { lo, hi } : null;
  }, [tournament]);

  const winner = tournament?.winner;
  const champion = tournament?.champion;
  // Deploy policy: with auto-deploy on, the champion ships itself (backend
  // first-deploy sweep, ~30s) — the manual deploy button is hidden and a
  // passive "deploying automatically" status shows instead. Manual actions
  // remain for user-selected models ("Train & deploy") and failure recovery.
  const autoDeploy = tournament?.auto_deploy === 'auto';
  const canPromote = champion?.model_id && champion?.training_id
    && (champion?.executed || champion?.training_status === 'completed'
        || champion?.status === 'trained');

  // ---- Model selection (deploy any scored model, not just the best) -------
  // Every entry that finished scoring is a candidate to deploy. They're
  // already rank-ordered (best first). The champion is the entry currently
  // materialized for serving — by default it's the winner.
  const deployableEntries = useMemo(
    () => entries.filter((e) => e.status === 'completed' && e.score != null),
    [entries],
  );
  const championPreset = champion?.preset_id || winner?.preset_id || null;
  // Hold the user's explicit pick; otherwise track the live champion so the
  // dropdown always defaults to "best" and follows an auto-fallback.
  useEffect(() => {
    setSelectedPreset((prev) => {
      if (prev && deployableEntries.some((e) => e.preset_id === prev)) {
        return prev;
      }
      return championPreset;
    });
  }, [championPreset, deployableEntries]);
  const effectivePreset = selectedPreset || championPreset;
  const selectedEntry = useMemo(
    () => deployableEntries.find((e) => e.preset_id === effectivePreset) || null,
    [deployableEntries, effectivePreset],
  );
  // True when the picked model IS the one already materialized for serving —
  // so the standard deploy/promote button applies. A different pick must be
  // prepared (trained) first via selectChampion before it can be deployed.
  const isSelectedChampion = Boolean(effectivePreset)
    && effectivePreset === championPreset;
  const selecting = selectState?.isLoading;
  // Whether the currently-materialized champion is the auto-selected best
  // (true rank-1 entry) — drives "Deploy best model" vs "Deploy this model"
  // copy. Uses the ranked leaderboard, NOT tournament.winner, because a
  // user/fallback selection rewrites winner to the chosen entry's label.
  const bestPreset = deployableEntries[0]?.preset_id || winner?.preset_id || null;
  const championIsWinner = !bestPreset || championPreset === bestPreset;
  // The winning model trains through the standard pipeline after the
  // tournament; if that training fails the deploy box must offer a recovery
  // path (re-fit) instead of an endless spinner.
  const championFailed = Boolean(champion)
    && !champion.executed
    && champion.training_status === 'failed';
  // The whole eligible lineup failed to train for serving — terminal.
  const exhausted = tournament?.champion_status === 'exhausted';
  // The user cancelled the champion's serving-training — a recoverable,
  // intentional stop (re-fit to try again). The backend does NOT auto-fall-
  // back in this case (see _champion_training_is_dead).
  const championCancelled = Boolean(champion)
    && !champion.executed
    && champion.training_status === 'cancelled';
  // Failed, but other passing entries remain: the backend auto-falls-back to
  // the next-best model, so we show "retrying" rather than a dead end.
  const fallingBack = championFailed && !exhausted;
  const fallbackReason = tournament?.champion_fallback_reason;
  const championTrainingNow = Boolean(champion)
    && champion.status === 'training'
    && !canPromote
    && !championFailed
    && !championCancelled;

  // Serving-training progress. Prefer the REAL backend progress
  // ({stage, pct} from training.job.progress, refetched live on each event)
  // over the old wall-clock guess. The clock fallback only kicks in before
  // the first progress tick arrives (or for legacy rows with no progress).
  const trainStartedAt = champion?.training_started_at;
  const trainElapsedMin = trainStartedAt
    ? (Date.now() - new Date(trainStartedAt).getTime()) / 60000
    : null;
  const trainSlow = championTrainingNow && trainElapsedMin != null
    && trainElapsedMin > SLOW_TRAIN_MIN;
  const realPct = typeof champion?.progress?.pct === 'number'
    ? champion.progress.pct : null;
  const realStage = champion?.progress?.stage || null;
  const trainPct = realPct != null
    ? Math.min(99, Math.max(2, Math.round(realPct)))
    : (trainElapsedMin != null
      ? Math.min(95, Math.max(6, Math.round((trainElapsedMin / EXPECTED_TRAIN_MIN) * 100)))
      : null);
  const STAGE_LABEL = {
    preparing: 'Preparing data',
    loading_data: 'Loading data',
    fitting: 'Fitting model',
    scoring: 'Scoring',
    saving: 'Saving model',
  };
  const stageLabel = realStage ? (STAGE_LABEL[realStage] || 'Working') : null;

  // Where the champion stands in the deploy lifecycle. The backend
  // attaches its latest promotion so the box reflects reality instead of
  // always offering "Deploy best model" after it's already serving.
  const deployment = champion?.deployment;
  const deployStatus = deployment?.status;
  const isDeployedLive = Boolean(deployment)
    && ['deployed', 'ready', 'promoted', 'dedicated'].includes(deployStatus);
  const isDeploying = Boolean(deployment)
    && ['starting', 'initializing', 'deploying', 'pending'].includes(deployStatus);
  const isDeployFailed = Boolean(deployment) && deployStatus === 'failed';
  // The autopilot's quality gate refused to auto-deploy this trained
  // champion (zero skill / range extrapolation). The backend only attaches
  // this while nothing is live or in flight, so it clears itself after a
  // manual "deploy anyway" or a passing retrain. The user overrides it with
  // the standard promote call (the gate only guards AUTOMATED deploys) or
  // picks a different model from the picker.
  const promotionBlocked = champion?.promotion_blocked;
  const isBlocked = Boolean(promotionBlocked?.reason)
    && !isDeployedLive && !isDeploying && !promoteState?.response;

  // Compact status roll-up for the summary box.
  const statusCounts = useMemo(() => {
    const list = tournament?.entries || [];
    return list.reduce((acc, e) => {
      acc[e.status] = (acc[e.status] || 0) + 1;
      return acc;
    }, {});
  }, [tournament]);

  // The model-fitting card only appears once a tournament exists (running
  // or done). Before that, the Activity chain owns the "fit" action — we
  // never show a competing "Fit models" CTA here.
  if (!tournament) return null;

  return (
    <Stack spacing={2}>
      {/* Header / status row */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          {active && <CircularProgress size={16} />}
          <Typography variant="subtitle2">
            {active
              ? 'Fitting models — results appear as they finish'
              : status === 'failed'
                ? 'Model fitting failed'
                : status === 'cancelled'
                  ? 'Fitting stopped'
                  : 'Model results'}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {active && (
            <Tooltip title="Stop the whole fitting run. Already-scored entries are kept, but no best model is selected — re-fit when ready." arrow>
              <span>
                <CMButtonOutlinedSmall
                  color="error"
                  startIcon={<StopCircleIcon />}
                  disabled={controlState?.isLoading && controlState?.target === 'stop'}
                  onClick={() => dispatch(tournamentControl({ goalId, op: 'stop' }))}
                >
                  Stop fitting
                </CMButtonOutlinedSmall>
              </span>
            </Tooltip>
          )}
          <Tooltip title={active ? 'Model fitting is already running' : 'Re-fit models on fresh data'} arrow>
            <span>
              <CMButtonOutlinedSmall
                startIcon={<ReplayIcon />}
                disabled={active || startState?.isLoading}
                onClick={() => dispatch(startTournament({ goalId }))}
              >
                Re-fit models
              </CMButtonOutlinedSmall>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <HowFittingWorks tournament={tournament} />

      {status === 'failed' && tournament?.error && (
        <Alert severity="error">{String(tournament.error)}</Alert>
      )}
      {startState?.error && (
        <Alert severity="error" onClose={() => dispatch(clearStart())}>
          {String(startState.error)}
        </Alert>
      )}
      {controlState?.error && (
        <Alert severity="error" onClose={() => dispatch(clearControl())}>
          {String(controlState.error)}
        </Alert>
      )}

      {/* Compact summary box — current/best model + status of all entries.
          Click to reveal the full leaderboard. */}
      <Card variant="outlined">
        <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            onClick={() => setShowAll((v) => !v)}
            sx={{ cursor: 'pointer' }}
          >
            <Box sx={{
              width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: (t) => `2px solid ${alpha(
                active ? t.palette.info.main
                  : status === 'failed' ? t.palette.error.main
                    : status === 'cancelled' ? t.palette.warning.main
                      : t.palette.success.main, 0.6)}`,
              backgroundColor: (t) => alpha(
                active ? t.palette.info.main
                  : status === 'failed' ? t.palette.error.main
                    : status === 'cancelled' ? t.palette.warning.main
                      : t.palette.success.main, 0.1),
            }}>
              {active
                ? <CircularProgress size={20} color="info" />
                : status === 'failed'
                  ? <ErrorOutlineIcon color="error" />
                  : status === 'cancelled'
                    ? <StopCircleIcon color="warning" />
                    : <CheckCircleIcon color="success" />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {active ? 'Currently fitting'
                  : status === 'failed' ? 'Fitting failed'
                    : status === 'cancelled' ? 'Fitting stopped'
                      : 'Best model'}
              </Typography>
              <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                {active
                  ? (() => {
                    const running = entries.find((e) => ACTIVE_ENTRY_STATUSES.has(e.status));
                    return running ? modelDisplayName(running) : 'Preparing…';
                  })()
                  : (winner ? modelDisplayName(winner) : '—')}
              </Typography>
            </Box>
            {/* Status pills — one dot per entry, coloured by outcome */}
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}>
              {entries.map((e) => {
                const st = ENTRY_STATUS_META[e.status] || ENTRY_STATUS_META.pending;
                return (
                  <Tooltip key={e.preset_id} title={`${modelDisplayName(e)}: ${st.label}`} arrow>
                    <Box sx={{
                      width: 9, height: 9, borderRadius: '50%',
                      bgcolor: (t) => (st.color === 'default'
                        ? t.palette.text.disabled
                        : t.palette[st.color]?.main || t.palette.text.disabled),
                      ...(ACTIVE_ENTRY_STATUSES.has(e.status) ? {
                        animation: 'cm-pulse 1.2s ease-in-out infinite',
                        '@keyframes cm-pulse': {
                          '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 },
                        },
                      } : {}),
                    }} />
                  </Tooltip>
                );
              })}
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {(statusCounts.completed || 0)}/{entries.length} scored
                {statusCounts.failed ? ` · ${statusCounts.failed} failed` : ''}
              </Typography>
              <IconButton size="small" sx={{ p: 0.25 }}>
                {showAll ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Stack>
          </Stack>
        </CardContent>

        <Collapse in={showAll} timeout="auto" unmountOnExit>
          <Divider />
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              '& th, & td': {
                borderColor: 'divider',
                px: { xs: 1, sm: 1.5 },
                py: 1,
              },
              '& th': {
                color: 'text.secondary', fontWeight: 600, fontSize: 10.5,
                textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap',
              },
            }}
          >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 32, display: { xs: 'none', sm: 'table-cell' } }}>#</TableCell>
              <TableCell>Entry</TableCell>
              <TableCell align="right" sx={{ width: 96 }}>
                <Tooltip arrow title={`How close each model's forecasts land to the actuals on held-out folds (100% − WAPE). Models are ranked by the goal's native metric (${metric?.name || 'score'}). Tap a row for per-fold detail.`}>
                  <span>Accuracy</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right" sx={{ width: 96 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((e) => {
              const isWinner = winner?.preset_id === e.preset_id;
              const engine = e.engine === 'foundation' && e.family
                ? { ...ENGINE_META.foundation, label: FAMILY_LABELS[e.family] || e.family }
                : (ENGINE_META[e.engine] || { label: e.engine });
              // A running entry that's been flagged for cancellation reads as
              // "Cancelling…" until its worker flips it to "Cancelled".
              const cancelling = Boolean(e.cancel_requested) && ACTIVE_ENTRY_STATUSES.has(e.status);
              const st = cancelling
                ? { label: 'Cancelling…', color: 'warning' }
                : (ENTRY_STATUS_META[e.status] || { label: e.status });
              const canCancelEntry = active
                && CANCELLABLE_ENTRY_STATUSES.has(e.status)
                && !cancelling;
              const cancelBusy = controlState?.isLoading
                && controlState?.target === e.preset_id;
              const techName = modelTechnicalName(e);
              return (
                <TableRow
                  key={e.preset_id}
                  hover
                  onClick={() => setDetailId(e.preset_id)}
                  sx={{
                    cursor: 'pointer',
                    ...(isWinner ? { bgcolor: (t) => `${t.palette.success.main}14` } : {}),
                  }}
                >
                  <TableCell sx={{ verticalAlign: 'middle', display: { xs: 'none', sm: 'table-cell' } }}>
                    {isWinner
                      ? <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      : (e.rank ?? '—')}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Tooltip title={techName ? `Technical name: ${techName}` : ''} arrow>
                        <Typography variant="body2" sx={{ fontSize: 13, fontWeight: isWinner ? 700 : 500, cursor: techName ? 'help' : 'inherit' }}>
                          {modelDisplayName(e)}
                        </Typography>
                      </Tooltip>
                      <Chip size="small" variant="outlined" color={engine.color} label={engine.label}
                        sx={{ height: 18, fontSize: 11 }} />
                      {e.flow === 'A' && (
                        <Tooltip
                          title={'The zero-shot foundation model: pretrained on millions of series, it sees ONLY the target\u2019s own history — none of your discovered drivers. Every driver-fed model has to beat this row to earn its keep.'}
                          arrow
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            color="warning"
                            label="Baseline"
                            sx={{ height: 18, fontSize: 11 }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                    {(() => {
                      const pct = scorePct(e, metric);
                      const isPctMetric = PERCENT_METRICS.has(String(metric?.name || '').toLowerCase());
                      return (
                        <>
                          {pct != null && !isPctMetric ? (
                            <AccuracyInfoTooltip
                              value={pct}
                              backtest={{ folds: (e.fold_scores || []).length }}
                              note={`Models are ranked by the goal's ${metric?.name || 'metric'} on held-out folds.`}
                            >
                              <Typography variant="body2" sx={{ fontSize: 13, fontWeight: isWinner ? 700 : 500, cursor: 'help' }}>
                                {fmtPct1(pct)}
                              </Typography>
                            </AccuracyInfoTooltip>
                          ) : (
                            <Tooltip
                              arrow
                              title={pct != null
                                ? `Score on the goal's ${metric?.name || 'metric'} (higher is better).`
                                : `Score on the goal's ${metric?.name || 'metric'} (lower is better).`}
                            >
                              <Typography variant="body2" sx={{ fontSize: 13, fontWeight: isWinner ? 700 : 500, cursor: 'help' }}>
                                {pct != null ? fmtPct1(pct) : fmtScore(e, metric)}
                              </Typography>
                            </Tooltip>
                          )}
                        </>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                    {ACTIVE_ENTRY_STATUSES.has(e.status) && !cancelling ? (
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                        <Box sx={{ display: 'inline-block', textAlign: 'left' }}>
                          <EntryFitProgress progress={e.progress} />
                        </Box>
                        {canCancelEntry && (
                          <Tooltip title="Cancel this fitting — the rest keep going and a best model is still chosen from whatever finishes" arrow>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={cancelBusy}
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  dispatch(tournamentControl({
                                    goalId, op: 'cancel_entry', presetId: e.preset_id,
                                  }));
                                }}
                                sx={{ p: 0.25 }}
                              >
                                {cancelBusy
                                  ? <CircularProgress size={12} color="inherit" />
                                  : <CloseIcon sx={{ fontSize: 14 }} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    ) : (
                      <Tooltip
                        arrow
                        title={e.error
                          ? String(e.error)
                          : `${st.label}${e.duration_s != null ? ` · fit in ${Math.round(e.duration_s)}s` : ''}`}
                      >
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          justifyContent="flex-end"
                          sx={{ cursor: 'help' }}
                        >
                          <Box
                            sx={{
                              width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                              bgcolor: st.color === 'default' ? 'text.disabled' : `${st.color}.main`,
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: { xs: 'none', sm: 'inline' }, whiteSpace: 'nowrap' }}
                          >
                            {st.label}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </Box>
        </Collapse>
      </Card>

      {/* Best model / deploy */}
      {status === 'completed' && champion && (
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1.5}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                {exhausted || championCancelled
                  ? <ErrorOutlineIcon sx={{ color: championCancelled ? 'warning.main' : 'error.main' }} />
                  : fallingBack || isDeploying
                    ? <CircularProgress size={20} />
                    : isDeployFailed
                      ? <ErrorOutlineIcon sx={{ color: 'error.main' }} />
                      : isBlocked
                        ? <ErrorOutlineIcon sx={{ color: 'warning.main' }} />
                        : isDeployedLive
                          ? <CloudDoneIcon sx={{ color: 'success.main' }} />
                          : <CheckCircleIcon sx={{ color: 'success.main' }} />}
                <Box sx={{ minWidth: 240 }}>
                  <Typography variant="subtitle2">
                    {championCancelled ? 'Training cancelled · '
                      : championTrainingNow ? (championIsWinner ? 'Training best model · ' : 'Training selected model · ')
                        : isDeployedLive ? 'Deployed · '
                          : isDeploying ? 'Deploying · '
                            : isBlocked ? 'Auto-deploy blocked · '
                              : (championIsWinner ? 'Best model: ' : 'Selected model: ')}{winner ? modelDisplayName(winner) : ''}
                  </Typography>
                  <Typography variant="caption" color={
                    trainSlow ? 'warning.main'
                      : isDeployFailed ? 'error.main'
                        : isBlocked ? 'warning.main' : 'text.secondary'
                  }>
                    {exhausted
                      ? (tournament?.champion_error
                         || 'Every passing model failed to train for serving. Re-fit on fresh data to try again.')
                      : championCancelled
                        ? 'You cancelled this model\u2019s training. Re-fit to choose and train a model for serving.'
                        : fallingBack
                        ? 'The top model couldn\u2019t be trained for serving — automatically retrying with the next-best model.'
                        : championTrainingNow
                          ? (trainSlow
                            ? 'Scoring is done — but this final training on your full dataset is taking longer than usual; the training service may be busy.'
                            : 'Scoring is done — this winner is now training on your full dataset so it can serve live predictions. Deploy unlocks when it lands.')
                          : isDeployFailed
                            ? 'Deployment failed — retry below.'
                            : isDeployedLive
                              ? 'Live — serving predictions.'
                              : isDeploying
                                ? 'Deploying… this finishes on its own.'
                                : isBlocked
                                  ? 'The quality gate held this model back — deploy it anyway, or pick a different model.'
                                  : autoDeploy
                                    ? 'Trained and ready — deploying automatically. No action needed.'
                                    : 'Trained and ready. Deployment goes through the standard gate and serves live predictions.'}
                    {champion.accuracy_score != null && !exhausted && !fallingBack
                      && !championTrainingNow && !isDeployedLive && !isDeploying
                      && ` Accuracy score: ${champion.accuracy_score}.`}
                  </Typography>

                  {/* Live serving-training progress */}
                  {championTrainingNow && (
                    <Box sx={{ mt: 0.75 }}>
                      <LinearProgress
                        variant={trainPct != null ? 'determinate' : 'indeterminate'}
                        value={trainPct ?? undefined}
                        color={trainSlow ? 'warning' : 'primary'}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {stageLabel
                          ? `${stageLabel}${trainPct != null ? ` · ${trainPct}%` : ''}`
                          : (trainElapsedMin != null ? `Running ${fmtElapsed(trainElapsedMin)}` : 'Starting…')}
                      </Typography>
                    </Box>
                  )}

                  {fallbackReason && !exhausted && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
                      {fallbackReason}
                    </Typography>
                  )}
                </Box>
              </Stack>

              {/* Deploy controls — model picker + its action, kept on the
                  status row so the card stays compact (no empty second row). */}
              {exhausted || championCancelled ? (
                <CMButton size="small"
                  startIcon={<ReplayIcon />}
                  disabled={active || startState?.isLoading}
                  onClick={() => dispatch(startTournament({ goalId }))}
                  sx={{ flexShrink: 0 }}
                >
                  Re-fit models
                </CMButton>
              ) : (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="flex-end"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ flexShrink: 0 }}
                >
                  {/* Model picker — deploy ANY scored model, not just the best.
                      Defaults to the best (rank-1) model; the user can switch. */}
                  {deployableEntries.length > 1 && (
                    <CMFormControlDark size="small" sx={{ minWidth: 230, mb: 0 }}>
                      <InputLabel id="deploy-model-label">Model to deploy</InputLabel>
                      <Select
                        labelId="deploy-model-label"
                        label="Model to deploy"
                        value={effectivePreset || ''}
                        onChange={(e) => {
                          dispatch(clearPromote());
                          dispatch(clearSelectChampion());
                          setSelectedPreset(e.target.value);
                        }}
                        disabled={selecting || isDeploying || championTrainingNow || fallingBack}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              mt: 0.5,
                              maxHeight: 360,
                              minWidth: 280,
                              borderRadius: 1.5,
                              border: (t) => `1px solid ${t.palette.divider}`,
                              backgroundImage: 'none',
                            },
                          },
                        }}
                        renderValue={(val) => {
                          const e = deployableEntries.find((x) => x.preset_id === val);
                          if (!e) return 'Choose a model';
                          const pct = scorePct(e, metric);
                          return (
                            <Stack direction="row" spacing={1} alignItems="baseline" sx={{ overflow: 'hidden' }}>
                              <Typography noWrap variant="body2" sx={{ fontWeight: 600 }}>{modelDisplayName(e)}</Typography>
                              {pct != null && (
                                <Typography variant="caption" color="text.secondary">{fmtPct1(pct)}</Typography>
                              )}
                            </Stack>
                          );
                        }}
                      >
                        {deployableEntries.map((e, idx) => {
                          const pct = scorePct(e, metric);
                          const isSel = e.preset_id === effectivePreset;
                          return (
                            <MenuItem
                              key={e.preset_id}
                              value={e.preset_id}
                              sx={{ borderRadius: 1, mx: 0.5, my: 0.25, py: 0.75 }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                  <Typography noWrap variant="body2" sx={{ fontWeight: isSel ? 700 : 500 }}>
                                    {modelDisplayName(e)}
                                  </Typography>
                                  {idx === 0 && (
                                    <Chip
                                      size="small"
                                      color="success"
                                      label="Best"
                                      sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                                    />
                                  )}
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                  {pct != null
                                    ? `${fmtPct1(pct)} accuracy`
                                    : (e.score != null ? `${metric?.name || 'score'} ${Number(e.score).toPrecision(4)}` : '')}
                                </Typography>
                              </Box>
                              {isSel && <CheckCircleIcon sx={{ ml: 1, fontSize: 18, color: 'success.main' }} />}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </CMFormControlDark>
                  )}

                  {(fallingBack || isDeploying) && <CircularProgress size={16} />}
                  {championTrainingNow && (
                    <Tooltip title="Stop training this model for serving. You can re-fit afterwards." arrow>
                      <span>
                        <CMButtonOutlined
                          color="error"
                          startIcon={<StopCircleIcon />}
                          disabled={controlState?.isLoading
                            && controlState?.target === 'cancel_training'}
                          onClick={() => dispatch(tournamentControl({
                            goalId, op: 'cancel_training',
                          }))}
                        >
                          Cancel training
                        </CMButtonOutlined>
                      </span>
                    </Tooltip>
                  )}
                  {!isSelectedChampion ? (
                    // A non-champion model is picked: it isn't trained for
                    // serving yet, so prepare it first (selectChampion). The
                    // board's training → Deploy lifecycle then takes over.
                    <Tooltip title={(championTrainingNow || isDeploying || fallingBack)
                      ? 'Finish the current model first, or cancel it'
                      : 'Prepare this model for serving, then deploy it'} arrow>
                      <span>
                        <CMButton size="small"
                          startIcon={selecting
                            ? <CircularProgress size={16} color="inherit" />
                            : <CloudUploadIcon />}
                          disabled={selecting || active || championTrainingNow || isDeploying || fallingBack}
                          onClick={() => {
                            dispatch(clearPromote());
                            dispatch(selectChampion({ goalId, presetId: effectivePreset }));
                          }}
                        >
                          {selecting ? 'Preparing…' : 'Train & deploy'}
                        </CMButton>
                      </span>
                    </Tooltip>
                  ) : isDeployedLive ? (
                    <Tooltip title="Deploy this model again" arrow>
                      <span>
                        <CMButtonOutlined
                          startIcon={<CloudUploadIcon />}
                          disabled={!canPromote || promoteState?.isLoading}
                          onClick={() => dispatch(promoteRequest({
                            modelId: champion.model_id,
                            trainingId: champion.training_id,
                            reason: championIsWinner ? 'Discovery best model' : 'Discovery selected model',
                          }))}
                        >
                          Re-deploy
                        </CMButtonOutlined>
                      </span>
                    </Tooltip>
                  ) : isBlocked ? (
                    // The quality gate refused the automatic deploy. Manual
                    // deploys skip the gate (a human made the call), so offer
                    // the override right here — the model picker above stays
                    // available for choosing a different model instead.
                    <Tooltip title="Override the quality gate and deploy this model" arrow>
                      <span>
                        <CMButton size="small"
                          startIcon={<CloudUploadIcon />}
                          disabled={!canPromote || promoteState?.isLoading}
                          onClick={() => dispatch(promoteRequest({
                            modelId: champion.model_id,
                            trainingId: champion.training_id,
                            reason: 'Discovery model — deployed over the quality-gate block',
                          }))}
                        >
                          Deploy anyway
                        </CMButton>
                      </span>
                    </Tooltip>
                  ) : autoDeploy && !isDeployFailed ? (
                    // Automated deployment: the backend ships the trained
                    // champion by itself (first-deploy sweep) — no button to
                    // click. A failed deploy still gets the manual retry
                    // below, and user-picked models keep "Train & deploy".
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ px: 0.5 }}>
                      {canPromote && !isDeploying && <CircularProgress size={14} />}
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {canPromote
                          ? 'Deploying automatically…'
                          : 'Auto-deploy on — goes live after training'}
                      </Typography>
                    </Stack>
                  ) : (
                    <Tooltip title={fallingBack
                      ? 'Training the next-best model after the top model failed'
                      : isDeploying ? 'Deployment is already in progress'
                        : championTrainingNow ? 'The selected model is still finishing training' : ''} arrow>
                      <span>
                        <CMButton size="small"
                          startIcon={<CloudUploadIcon />}
                          disabled={!canPromote || promoteState?.isLoading || isDeploying}
                          onClick={() => dispatch(promoteRequest({
                            modelId: champion.model_id,
                            trainingId: champion.training_id,
                            reason: championIsWinner ? 'Discovery best model' : 'Discovery selected model',
                          }))}
                        >
                          {isDeployFailed ? 'Retry deploy'
                            : championIsWinner ? 'Deploy best model' : 'Deploy this model'}
                        </CMButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              )}
            </Stack>
            {!isSelectedChampion && !selecting && !selectState?.error && selectedEntry && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                {`${modelDisplayName(selectedEntry)} isn\u2019t prepared for serving yet — `}
                <b>Train &amp; deploy</b>
                {' builds it on your full dataset, then it goes live through the standard gate.'}
              </Alert>
            )}
            {isBlocked && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {'Auto-deploy was blocked: '}
                <b>{promotionBlocked.reason}</b>
                {'. It scored well on accuracy but the gate protects serving from models '}
                {'that add no real signal. You can '}
                <b>Deploy anyway</b>
                {' to override, or pick a different model above.'}
              </Alert>
            )}
            {selectState?.error && (
              <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => dispatch(clearSelectChampion())}>
                {String(selectState.error)}
              </Alert>
            )}
            {promoteState?.error && (
              <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => dispatch(clearPromote())}>
                {String(promoteState.error)}
              </Alert>
            )}
            {promoteState?.response && (
              <Alert severity="success" sx={{ mt: 1.5 }}>
                {championIsWinner
                  ? 'Deployment started — the best model is going live.'
                  : 'Deployment started — your selected model is going live.'}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      <EntryDetailDialog
        entry={detailEntry}
        metric={metric}
        lower={lower}
        foldRange={foldRange}
        isWinner={Boolean(detailEntry && winner?.preset_id === detailEntry.preset_id)}
        onClose={() => setDetailId(null)}
      />
    </Stack>
  );
};

export default TournamentBoard;
